import PropTypes from 'prop-types'
import {useOutreachFilters} from '../../../../../store/hooks/useOutreachFilters'
import {getOutreachTileLayerTooltipStats, dimensions} from '../../../helper'
import {useConfig} from '../../../../../store/hooks/config'
import {
  StyledBoxTooltip,
  StyledPaperTooltip,
  StyledSpaceBox,
  StyledDividerTooltip,
  StyledLabelMedium,
  StyledBenefitsEligibilityIconContainer,
  StyledSubtitle2,
  StyledBody1,
  StyledTooltipData,
  StyledSubtitle1,
  StyledCircleIcon,
  StyledBody2,
  StyledAlignmentStack,
  StyledAlignCenterStack,
  StyledLabelSmall,
  StyledBracketContainer
} from './Styles'
import {renderPersonaTooltip} from './renderPersonaTooltip'
import {ReactComponent as BenefitsEligibilityIcon} from '../../../../../icons/map/tooltip/BenefitsEligibilityIcon.svg'
import {ReactComponent as LocationIcon} from '../../../../../icons/map/tooltip/locationIcon.svg'

import {useMemo} from 'react'
import {checkIsFilterApplied} from '../helpers'
import {Grid, Stack} from '@mui/material'
import useFilterManagement from '../../../../../store/hooks/useFilterManagement'

const TileLayerTooltip = ({currentView, data, personaData}) => {
  // New filter management system for outreach
  const {isOutreachActive, activeOutreachType, enrolledValue, selectedOptionsWithConfig} = useOutreachFilters()
  const {personasConfig} = useConfig()
  const {appliedFilters} = useFilterManagement()
  // Memoize filter applied check
  const isFilter = useMemo(() => {
    return checkIsFilterApplied(appliedFilters)
  }, [appliedFilters])

  const getViewType = (currentViewId) => {
    if (currentViewId?.startsWith('counties-fill')) return 'county'
    if (currentViewId?.startsWith('congressionaldistrict-fill')) return 'congressionalDistrict'

    switch (currentViewId) {
      case 'state-fill':
        return 'state'
      case 'counties-fill':
        return 'county'
      case 'congressionaldistrict-fill':
        return 'congressionalDistrict'
      case 'censustract-fill':
      case 'censustract-fill1':
      case 'censustract-fill2':
      case 'censustract-fill3':
      case 'censustract-fill4':
      case 'censustract-filln':
        return 'censusTract'
      default:
        return 'state'
    }
  }

  const view = getViewType(currentView.id)

  const getTitle = () => {
    switch (view) {
      case 'state':
        return data?.state
      case 'county':
        return data?.county
      case 'congressionalDistrict':
        return 'Cong. District ' + data?.congressional_district
      case 'censusTract':
        return 'Tract ID: ' + data?.censusTractId
      default:
        return 'N/A'
    }
  }

  const title = getTitle()

  const TooltipData = {
    title,
    subTitle: data?.state,
    ctCounty: data?.county
  }

  const snapOptions = {
    0: 'No',
    1: 'Yes',
    2: 'All'
  }

  const getOutreachTileLayerTooltipData = () => {
    const tooltipInfo = getOutreachTileLayerTooltipStats(activeOutreachType, selectedOptionsWithConfig)
    const snapStatus = snapOptions[enrolledValue] || 'All'

    return (
      <>
        <StyledSpaceBox size={15} />
        <StyledLabelMedium>Benefits Eligibility stats</StyledLabelMedium>
        <StyledDividerTooltip />

        <StyledSpaceBox size={15} />
        <Stack direction={'row'} spacing={2}>
          <Grid container spacing={2}>
            <Grid xs={1}>
              <StyledBenefitsEligibilityIconContainer>
                <BenefitsEligibilityIcon />
              </StyledBenefitsEligibilityIconContainer>
            </Grid>
            <Grid xs={3}>
              <StyledSubtitle2>Program</StyledSubtitle2>
            </Grid>
            <Grid xs={4}>
              <StyledSubtitle2>Eligible for</StyledSubtitle2>
            </Grid>
            {(snapStatus === 'All' || snapStatus === 'Yes') && (
              <Grid xs={4}>
                <StyledSubtitle2>Enrolled in</StyledSubtitle2>
              </Grid>
            )}
            {snapStatus === 'No' && (
              <Grid xs={4}>
                <StyledSubtitle2>Not Enrolled in</StyledSubtitle2>
              </Grid>
            )}
          </Grid>
        </Stack>

        <Stack direction={'row'} spacing={2}>
          {tooltipInfo?.data?.map((item) => {
            return (
              <Grid container spacing={2} key={item.key}>
                <Grid xs={1} />
                <Grid xs={3}>
                  <StyledBody1>{item.name.split(' ')[0]}</StyledBody1>
                </Grid>
                <Grid xs={4}>
                  <StyledTooltipData>{data?.[item.eligible] || 0}</StyledTooltipData>
                </Grid>
                {(snapStatus === 'All' || snapStatus === 'Yes') && (
                  <Grid xs={4}>
                    <StyledTooltipData>{data?.[item.enrolled] || 0}</StyledTooltipData>
                  </Grid>
                )}
                {snapStatus === 'No' && (
                  <Grid xs={4}>
                    <StyledTooltipData>{data?.[item.notEnrolled] || 0}</StyledTooltipData>
                  </Grid>
                )}
              </Grid>
            )
          })}
        </Stack>
      </>
    )
  }

  const tooltipSectionHeader = view === 'censusTract' ? 'Population Overview' : 'Geo-population stats'

  return (
    <StyledBoxTooltip tooltipWidth={415}>
      <StyledPaperTooltip>
        {data && (
          <>
            {/* GENERAL GEO-POPULATION STATS TOOLTIP HERE */}
            <Stack direction='column' spacing={1}>
              <Stack direction='row' alignItems='center'>
                <LocationIcon />
                <StyledSubtitle1>{TooltipData?.title}</StyledSubtitle1>
                {view !== 'state' && view !== 'censusTract' && (
                  <>
                    <StyledCircleIcon />
                    <StyledBody2>{TooltipData?.subTitle}</StyledBody2>
                  </>
                )}
                {view === 'censusTract' && (
                  <>
                    <StyledCircleIcon />
                    <StyledBody2>{TooltipData?.ctCounty}</StyledBody2>
                    <StyledCircleIcon />
                    <StyledBody2>{TooltipData?.subTitle}</StyledBody2>
                  </>
                )}
              </Stack>
              <StyledSpaceBox size={0} />
              <StyledLabelMedium>{tooltipSectionHeader}</StyledLabelMedium>
              <StyledDividerTooltip />

              <StyledSpaceBox size={4} />
              <Stack
                direction='row'
                spacing={isFilter ? undefined : 4}
                component={isFilter ? StyledAlignmentStack : Stack}
              >
                <Stack direction='column' spacing={0.5}>
                  <StyledBody1>{dimensions[view]} Adults</StyledBody1>
                  <StyledAlignCenterStack direction='row' spacing={1}>
                    <StyledTooltipData>{isFilter ? data?.[`${view}Population`] : data?.population}</StyledTooltipData>
                    {!isFilter && view !== 'censusTract' && (
                      <StyledLabelSmall>
                        ({view === 'state' ? data?.usPercentage : data?.statePercentage}% of{' '}
                        {view === 'state' ? 'US' : 'State'})
                      </StyledLabelSmall>
                    )}
                    {!isFilter && view === 'censusTract' && (
                      <StyledLabelSmall>({data?.countyPercentage}% of County)</StyledLabelSmall>
                    )}
                  </StyledAlignCenterStack>
                </Stack>
                {isFilter && (
                  <Stack direction='column' spacing={0.5}>
                    <StyledBody1>Selected Adult Pop.</StyledBody1>
                    {view === 'state' ? (
                      <StyledAlignCenterStack direction='row' spacing={1}>
                        <StyledTooltipData>{data?.population}</StyledTooltipData>
                        <StyledLabelSmall> {data?.statePercentage}% of State</StyledLabelSmall>
                      </StyledAlignCenterStack>
                    ) : (
                      <Stack direction='row' spacing={1}>
                        <StyledTooltipData>{data?.population}</StyledTooltipData>
                        <StyledBracketContainer direction='column'>
                          <StyledLabelSmall>
                            {' '}
                            {data[`${view}Percentage`]}% of {dimensions[view]}
                          </StyledLabelSmall>
                          {view !== 'censusTract' && (
                            <StyledLabelSmall> {data?.statePercentage}% of State</StyledLabelSmall>
                          )}
                          {view === 'censusTract' && (
                            <StyledLabelSmall> {data?.countyPercentage}% of County</StyledLabelSmall>
                          )}
                        </StyledBracketContainer>
                      </Stack>
                    )}
                  </Stack>
                )}
                <Stack direction='column' spacing={0.5}>
                  <StyledBody1>Unique Addresses</StyledBody1>
                  <StyledTooltipData>{data?.count}</StyledTooltipData>
                </Stack>
              </Stack>
            </Stack>

            {/* PERSONA TOOLTIP HERE */}
            {personaData && renderPersonaTooltip(personaData, view, personasConfig)}

            {/* OUTREACH SIMULATION STATS TOOLTIP HERE */}
            {isOutreachActive ? <>{getOutreachTileLayerTooltipData()}</> : null}
          </>
        )}
      </StyledPaperTooltip>
    </StyledBoxTooltip>
  )
}

TileLayerTooltip.propTypes = {
  data: PropTypes.object,
  personaData: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  currentView: PropTypes.object.isRequired
}

export default TileLayerTooltip
